import { useContext, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { ModalContext } from "../../context/ModalContextProvider";
import { ModalType } from "../../context/modalType";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";
import { TaskTypesRoutes } from "../../../home/constants/taskTypesRoutes";

import {
  NavigationItemsConfig,
  NavigationItem,
  TaskItem,
  MenuIdTypes,
  MultiOrderAddOnItem,
  TeamsManagerItem,
} from "./constants/navigationItemsConfig";

import styles from "./main-navigation.module.css";

import { isRDTDevice, isRDTDevice11 } from "@coworker/reusable/helpers/devices";
import { FixaListView } from "../../../shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../shared/wrappers/FixaListView/FixaListViewItem";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { sortMenuItems } from "../../../shared/helpers/navigation.helper";
import { ProductQualityError } from "../../../shared/components/ProductQualityError/productQualityError";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import { UserRoles } from "@coworker/apprestructured/src/shared/enums/user/userRoles";

const MainNavigation = () => {
  const navigate = useFixaNavigate();
  const { t } = useTranslation();
  const { modalState, toggleModal } = useContext(ModalContext);
  const qualityTeamId = useProductQualityTeamId();
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const isRDT11 = isRDTDevice11();
  const isRDT = isRDTDevice();

  const handleToggleMainNavigation = () => {
    toggleModal({ type: ModalType.MainNavigation });
  };
  const handleNavigationLinkClick = async (type: string) => {
    if (type === TaskTypesRoutes.PRODUCT_QUALITY && !qualityTeamId) {
      setOpenErrorModal(true);
      return;
    }

    if (type === TaskTypesRoutes.CUSTOM) {
      navigate(`/task/new/custom`);
      handleToggleMainNavigation();
      return;
    }

    navigate(`/scanner/${type}`);
    handleToggleMainNavigation();
  };

  const filteredNavigationItemsConfig =
    isRDT || isRDT11
      ? NavigationItemsConfig.filter((item) => item.id !== "dashboard")
      : NavigationItemsConfig;

  const navigationItems = filteredNavigationItemsConfig;

  // Load saved order indexes from localStorage
  const savedOrderIndexes = localStorage.getItem("navigationOrderIndexes");
  if (savedOrderIndexes) {
    const parsedOrderIndexes = JSON.parse(savedOrderIndexes);
    navigationItems.forEach((item) => {
      const savedOrderIndex = parsedOrderIndexes[item.id];
      if (savedOrderIndex !== undefined) {
        item.orderIndex = savedOrderIndex;
      }
    });
  }

  // Increment order index for the clicked element and save it to localStorage
  const updateNavigationLinkOrder = (id: string) => {
    const item = navigationItems.find((item) => item.id === id);
    if (item) {
      item.orderIndex++;
      localStorage.setItem(
        "navigationOrderIndexes",
        JSON.stringify(
          navigationItems.reduce((acc: { [key: string]: number }, item) => {
            acc[item.id] = item.orderIndex;
            return acc;
          }, {})
        )
      );
    }
  };

  const handleItemClick = async (item: NavigationItem) => {
    if (item.href) {
      navigate(item.href);
    }

    if (item.link) {
      window.open(item.link, "_blank", "noopener,noreferrer");
    }

    if (item.id === MenuIdTypes.TASKS) {
      trackerHelper.trackTaskLinkClickedInMenu();
    }
    updateNavigationLinkOrder(item.id);
    handleToggleMainNavigation();
  };

  const { getRoleId } = useLoggedInUser();
  const enableTeamsManagerNavigation = [
    UserRoles.COUNTRY_SUPERUSER,
    UserRoles.STOREOWNER,
    UserRoles.SUPERUSER,
  ].includes(getRoleId() as UserRoles);

  return (
    <FixaModal
      visible={modalState.isMainNavigationOpen}
      keepContentMounted={true}
      handleCloseBtn={handleToggleMainNavigation}
    >
      <FixaSheets
        header={null}
        footer={null}
        preserveAlignment={false}
        size="small"
        alignment="left"
      >
        <FixaModalHeader
          title={t("menuString")}
          closeBtnClick={() => handleToggleMainNavigation()}
        />
        <FixaListView
          id="main-navigation"
          className={styles["list-view"] as string}
        >
          {sortMenuItems(navigationItems, TaskItem, MultiOrderAddOnItem).map(
            (item) => (
              <FixaListViewItem
                inset
                onClick={() => {
                  if (item.taskType) {
                    handleNavigationLinkClick(item.taskType);
                  } else {
                    handleItemClick(item);
                  }
                }}
                key={item.id}
                emphasised
                control="navigational"
                controlIcon={null}
                id={item.id}
                leadingIcon={item.icon}
                title={t(item.labelTranslationKey)}
                size="small"
                data-testid={item.testId}
              />
            )
          )}
          <ProductQualityError
            openProductQualityErrorModal={openErrorModal}
            onClose={setOpenErrorModal}
          />
          {enableTeamsManagerNavigation && (
            <FixaListViewItem
              inset
              onClick={() => handleItemClick(TeamsManagerItem)}
              emphasised
              control="navigational"
              controlIcon={null}
              leadingIcon={TeamsManagerItem.icon}
              id={TeamsManagerItem.id}
              title={t(TeamsManagerItem.labelTranslationKey)}
              size="small"
              data-testid={"teams-manager"}
            />
          )}
        </FixaListView>
      </FixaSheets>
    </FixaModal>
  );
};

export default MainNavigation;
